import themesCatalog from 'themes/themes.json';
import themeLabelsCatalog from 'themes/theme-labels.json';

export type ThemeId =
  | 'classic'
  | 'ocean'
  | 'sandstone'
  | 'midnight'
  | 'emerald'
  | 'amethyst'
  | 'graphite'
  | 'onyx'
  | 'monochrome';

export type ThemeTokens = Record<string, string>;

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  description: string;
  tokens: ThemeTokens;
}

export const THEME_STORAGE_KEY = 'bepro.ui.theme';
export const THEMES = themesCatalog as unknown as ThemeDefinition[];
export const themeLabels = themeLabelsCatalog as Record<
  string,
  { label: string; names: string[] }
>;

const APPLIED_COLOR_KEYS = [
  '--color-danger',
  '--color-warning',
  '--color-danger-bg',
  '--color-warning-bg',
  '--color-0',
  '--color-100',
  '--color-120',
  '--color-150',
  '--color-180',
  '--color-200',
  '--color-300',
  '--color-400',
  '--color-500',
  '--color-600',
  '--color-700',
  '--color-contrast',
  '--color-contrast-dark',
  '--color-contrast-2',
  '--color-contrast-rgb',
  '--color-contrast-light',
  '--color-contrast-light-hex',
  '--color-accent',
  '--color-success',
  '--color-success-light',
  '--color-text',
  '--color-text-light',
  '--color-text-lightest',
  '--color-bg',
  '--color-bg-light',
  '--color-dark',
  '--color-hover',
  '--color-border',
  '--color-client',
  '--color-filter-bg',
  '--color-on-primary',
  '--color-btn-primary',
  '--color-btn-danger',
  '--color-btn-success',
  '--color-btn-warning',
  '--color-btn-fg',
] as const;

export function themeId(value: unknown): ThemeId {
  return THEMES.some((theme) => theme.id === value) ? (value as ThemeId) : 'classic';
}

export function readTheme(): ThemeId {
  try {
    return themeId(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'classic';
  }
}

function hexToRgbChannels(hex: string): string | null {
  const value = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function mixHexToward(hex: string, toward: string, amount: number): string {
  const parse = (value: string) => {
    const clean = value.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
    return [
      parseInt(clean.slice(0, 2), 16),
      parseInt(clean.slice(2, 4), 16),
      parseInt(clean.slice(4, 6), 16),
    ] as const;
  };
  const from = parse(hex);
  const to = parse(toward);
  if (!from || !to) return hex;
  const mix = (a: number, b: number) => Math.round(a + (b - a) * amount);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(from[0], to[0]))}${toHex(mix(from[1], to[1]))}${toHex(mix(from[2], to[2]))}`;
}

function clearAppliedThemeVars() {
  const root = document.documentElement;
  APPLIED_COLOR_KEYS.forEach((key) => root.style.removeProperty(key));
}

function mapBoTokensToAppColors(tokens: ThemeTokens): Record<string, string> {
  const primary = tokens['--bo-primary'];
  const primaryHover = tokens['--bo-primary-hover'] || primary;
  const primaryTint = tokens['--bo-primary-tint'];
  const navy = tokens['--bo-navy'];
  const text = tokens['--bo-text'];
  const muted = tokens['--bo-muted'];
  const subtle = tokens['--bo-subtle'] || muted;
  const bg = tokens['--bo-bg'];
  const surface = tokens['--bo-surface'];
  const surfaceAlt = tokens['--bo-surface-alt'] || bg;
  const border = tokens['--bo-border'];
  const hover = tokens['--bo-hover'];
  const green = tokens['--bo-green'];
  const greenTint = tokens['--bo-green-tint'];
  const red = tokens['--bo-red'];
  const redTint = tokens['--bo-red-tint'];
  const orange = tokens['--bo-orange'];
  const orangeTint = tokens['--bo-orange-tint'];
  const filterBg = tokens['--bo-filter-bg'];
  const onPrimary = tokens['--bo-on-primary'] || '#ffffff';
  const onNav = tokens['--bo-on-nav'] || '#ffffff';
  const rgb = navy ? hexToRgbChannels(navy) : null;

  const mapped: Record<string, string> = {};

  const isDark = tokens['--bo-scheme'] === 'dark';
  const deepPrimary = primary
    ? isDark
      ? mixHexToward(primary, '#0d4a8c', 0.68)
      : primary
    : undefined;
  const deepPrimaryHover = primaryHover
    ? isDark
      ? mixHexToward(primaryHover, '#08366a', 0.62)
      : primaryHover
    : deepPrimary;
  const deepRed = red
    ? isDark
      ? mixHexToward(red, '#9b2430', 0.62)
      : red
    : undefined;
  const deepGreen = green
    ? isDark
      ? mixHexToward(green, '#145c38', 0.6)
      : green
    : undefined;
  const deepOrange = orange
    ? isDark
      ? mixHexToward(orange, '#8a520f', 0.58)
      : orange
    : undefined;

  if (primary) mapped['--color-accent'] = primary;
  if (deepPrimary) {
    mapped['--color-contrast'] = deepPrimary;
    mapped['--color-client'] = deepPrimary;
  }
  if (deepPrimaryHover) mapped['--color-contrast-dark'] = deepPrimaryHover;
  if (navy) mapped['--color-contrast-2'] = navy;
  if (rgb) mapped['--color-contrast-rgb'] = rgb;
  if (primaryTint) {
    mapped['--color-contrast-light-hex'] = primaryTint;
    mapped['--color-contrast-light'] = primaryTint;
  }
  if (text) {
    const softText = isDark
      ? mixHexToward(text, muted || '#9aa8b8', 0.42)
      : text;
    mapped['--color-text'] = softText;
    mapped['--color-dark'] = softText;
    mapped['--color-500'] = softText;
    mapped['--color-600'] = softText;
    mapped['--color-700'] = softText;
  }
  if (muted) {
    mapped['--color-text-light'] = muted;
    mapped['--color-400'] = muted;
  }
  if (subtle) mapped['--color-300'] = subtle;
  if (bg) {
    mapped['--color-bg'] = bg;
    mapped['--color-100'] = bg;
  }
  if (surface) {
    mapped['--color-0'] = surface;
    mapped['--color-bg-light'] = surface;
  }
  if (surfaceAlt) mapped['--color-120'] = surfaceAlt;
  if (hover) {
    mapped['--color-hover'] = hover;
    mapped['--color-150'] = hover;
  }
  if (border) {
    mapped['--color-border'] = border;
    mapped['--color-180'] = border;
    mapped['--color-200'] = border;
  }
  if (green) mapped['--color-success'] = green;
  if (greenTint) mapped['--color-success-light'] = greenTint;
  if (red) mapped['--color-danger'] = red;
  if (redTint) mapped['--color-danger-bg'] = redTint;
  if (orange) mapped['--color-warning'] = orange;
  if (orangeTint) mapped['--color-warning-bg'] = orangeTint;
  if (onPrimary) mapped['--color-on-primary'] = onPrimary;

  mapped['--color-text-lightest'] = isDark
    ? mixHexToward(onNav, muted || '#a8b6c8', 0.4)
    : onNav;

  if (isDark) {
    mapped['--color-filter-bg'] = filterBg || '#2e302a';
    if (deepPrimary) mapped['--color-btn-primary'] = deepPrimary;
    if (deepRed) mapped['--color-btn-danger'] = deepRed;
    if (deepGreen) mapped['--color-btn-success'] = deepGreen;
    if (deepOrange) mapped['--color-btn-warning'] = deepOrange;
    mapped['--color-btn-fg'] = '#e6edf5';
    mapped['--color-on-primary'] = '#e6edf5';
  } else {
    mapped['--color-filter-bg'] = '#ffffe0';
    if (deepPrimary) mapped['--color-btn-primary'] = deepPrimary;
    if (deepRed) mapped['--color-btn-danger'] = deepRed;
    if (deepGreen) mapped['--color-btn-success'] = deepGreen;
    if (deepOrange) mapped['--color-btn-warning'] = deepOrange;
    mapped['--color-btn-fg'] = onPrimary;
  }

  return mapped;
}

export function applyTheme(id: ThemeId) {
  const theme = THEMES.find((item) => item.id === id) || THEMES[0];
  const root = document.documentElement;

  root.dataset.theme = theme.id;

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme.id);
  } catch {
    /* ignore quota / private mode */
  }

  clearAppliedThemeVars();

  if (theme.id === 'classic') {
    root.style.colorScheme = 'light';
    return theme.id;
  }

  const mapped = mapBoTokensToAppColors(theme.tokens);
  Object.entries(mapped).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  root.style.colorScheme = theme.tokens['--bo-scheme'] === 'dark' ? 'dark' : 'light';
  return theme.id;
}

applyTheme(readTheme());
